import { Combobox } from '@headlessui/react';
import ConcatText from 'components/ConcatText';
import { MIN_NUMBER_SEARCH, TEXT_STRING } from '../../constants';
import Text from 'components/Text';
import InfiniteScroll from 'react-infinite-scroll-component';
import Icon from 'components/Icon';
import Avatar from 'components/Avatar';

type InputSelectItem = {
  id: number;
  nickname: string;
  avatar?: string | null;
};

type Props<T extends InputSelectItem> = {
  data: T[];
  searchValue: string;
  onSearchValue: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSelectValue: (value: T) => void;
  selectedData: Pick<T, 'id'>[];
  placeholder?: string;
  fetchMore: () => void;
  hasMore: boolean;
  loading?: boolean;
  disabled?: boolean;
  disabledOptions?: boolean;
  optionsVisible?: boolean;
};

function InputSelect<T extends InputSelectItem>({
  data,
  selectedData,
  onSearchValue,
  placeholder = '',
  onSelectValue,
  searchValue,
  hasMore,
  fetchMore,
  loading = false,
  disabled = false,
  disabledOptions = false,
  optionsVisible,
}: Props<T>) {
  const optionsDisabled = disabled || disabledOptions;
  const shouldShowOptions =
    !disabled &&
    (optionsVisible ?? searchValue.trim().length >= MIN_NUMBER_SEARCH);

  return (
    <Combobox disabled={disabled}>
      <div className="relative w-full">
        <Combobox.Button className="w-full">
          <Combobox.Input
            autoComplete="off"
            value={searchValue}
            placeholder={placeholder}
            onChange={onSearchValue}
            className={
              'w-full flex items-center py-3.5 rounded-lg ring-1 ring-neutral-300 focus-within:ring-2 focus-within:ring-primary border-0 bg-transparent placeholder:text-neutral-300'
            }
          />
        </Combobox.Button>
        <div
          className={`${
            shouldShowOptions ? 'block' : 'hidden'
          } absolute z-10 w-full mt-3 `}
        >
          <Combobox.Options
            className={`w-full h-full max-h-40 overflow-y-auto flex flex-col border border-solid border-neutral-400 rounded-xl shadow-xl bg-white`}
            id="scrollOptions"
          >
            {loading ? (
              <>
                {Array.from(Array(2)).map((_, index) => (
                  <div
                    key={index}
                    className="flex w-full items-center gap-4 p-4 pointer-events-none"
                  >
                    <div className="w-10 aspect-square rounded-full animate-pulse bg-neutral-400"></div>
                    <div className="flex-1 h-6 animate-pulse bg-neutral-400"></div>
                  </div>
                ))}
              </>
            ) : (
              <>
                {!!data?.length ? (
                  <InfiniteScroll
                    next={fetchMore}
                    hasMore={hasMore}
                    loader={
                      <div className="flex w-full items-center gap-4 p-4 pointer-events-none">
                        <div className="w-10 aspect-square rounded-full animate-pulse bg-neutral-400"></div>
                        <div className="flex-1 h-6 animate-pulse bg-neutral-400"></div>
                      </div>
                    }
                    dataLength={data?.length}
                    scrollThreshold={0.5}
                    scrollableTarget="scrollOptions"
                  >
                    <>
                      {data.map((item) => {
                        const isSelected = selectedData.some(
                          ({ id }) => id === item.id,
                        );
                        return (
                          <Combobox.Option
                            key={item.id}
                            value={item.id}
                            className={`${
                              optionsDisabled &&
                              'bg-neutral-50 pointer-events-none cursor-not-allowed'
                            } flex w-full items-center gap-4 p-4 cursor-pointer hover:bg-primary-50`}
                            onClick={() => {
                              if (optionsDisabled) return;
                              !isSelected && onSelectValue(item);
                            }}
                          >
                            <Avatar
                              url={item.avatar ?? undefined}
                              size="w-10"
                              circle
                            />
                            <ConcatText
                              prefixText={item.nickname}
                              suffixText={item.id}
                              className="text-[14px] font-bold flex-1"
                            />
                            {isSelected && <Icon icon="check" />}
                          </Combobox.Option>
                        );
                      })}
                    </>
                  </InfiniteScroll>
                ) : (
                  <Combobox.Option
                    value={undefined}
                    className="flex w-full items-center gap-4 p-4 cursor-pointer pointer-events-none"
                  >
                    <Text center bold>
                      {TEXT_STRING.COMMON.NOT_FOUND_GIRL}
                    </Text>
                  </Combobox.Option>
                )}
              </>
            )}
          </Combobox.Options>
        </div>
      </div>
    </Combobox>
  );
}

export default InputSelect;
